import { Box, Typography, Tooltip, alpha, useTheme } from '@mui/material'
import { Check, Close, Visibility, MeetingRoom, Settings as SettingsIcon, Lock, Tune, Leaderboard, FormatListNumbered } from '@mui/icons-material'
import { AdventureCategoryDto, CriteriaTypeDto, NodeTypeDto, StatisticTypes } from '@api/types/adventure'

interface CategoryComparisonTableProps {
  categories: AdventureCategoryDto[]
  criteriaTypes: CriteriaTypeDto[]
  nodeTypes: NodeTypeDto[]
  selectedCategoryId: number | null
  onCategorySelect: (categoryId: number) => void
}

export default function CategoryComparisonTable({
  categories,
  criteriaTypes,
  nodeTypes,
  selectedCategoryId,
  onCategorySelect,
}: CategoryComparisonTableProps) {
  const theme = useTheme()

  // Filter out Start node - all flows have it by default
  const filteredNodeTypes = nodeTypes.filter(
    (node) => node.name.toLowerCase() !== 'start' && node.displayName.toLowerCase() !== 'start'
  )

  // Settings fields to display
  // triState fields: false = forced off, null = creator choice, true = forced on
  const settingsFields = [
    { key: 'workflowRequired', label: 'Workflow Required', triState: false },
    { key: 'isPublicAllowed', label: 'Public/Private', triState: true, falseLabel: 'Private Only', nullLabel: 'Creator Choice', trueLabel: 'Public Only' },
    { key: 'groupsAllowed', label: 'Groups', triState: true, falseLabel: 'Solo Only', nullLabel: 'Creator Choice', trueLabel: 'Groups Only' },
    { key: 'repeatableAllowed', label: 'Repeatable', triState: true, falseLabel: 'One-time Only', nullLabel: 'Creator Choice', trueLabel: 'Repeatable Only' },
    { key: 'statisticTypes', label: 'Statistics', isStatistic: true },
  ]

  // Helper to check if a criteria type is allowed for a category
  const getCriteriaSupport = (category: AdventureCategoryDto, criteriaId: number) => {
    const display = category.allowedDisplayCriteria?.includes(criteriaId) ?? false
    const join = category.allowedJoinCriteria?.includes(criteriaId) ?? false
    const logic = category.allowedLogicCriteria?.includes(criteriaId) ?? false
    return { display, join, logic }
  }

  // Helper to check if a node type is allowed for a category
  const isNodeAllowed = (category: AdventureCategoryDto, nodeId: number) => {
    return category.allowedNodes?.includes(nodeId) ?? false
  }

  // Get setting value from category
  const getSettingValue = (category: AdventureCategoryDto, key: string): boolean | null => {
    const value = category[key as keyof AdventureCategoryDto]
    if (typeof value === 'boolean') return value
    if (value === null || value === undefined) return null
    return Boolean(value)
  }

  const renderBooleanCell = (value: boolean | null) => {
    if (value === null) return <Close sx={{ color: 'text.disabled', fontSize: 18 }} />
    return value ? (
      <Check sx={{ color: 'success.main', fontSize: 20 }} />
    ) : (
      <Close sx={{ color: 'error.main', fontSize: 18 }} />
    )
  }

  // Render tri-state setting cell (false = forced off, null = creator choice, true = forced on)
  const renderTriStateCell = (value: boolean | null, falseLabel: string, nullLabel: string, trueLabel: string) => {
    if (value === null) {
      return (
        <Tooltip title={nullLabel}>
          <Tune sx={{ color: 'info.main', fontSize: 20 }} />
        </Tooltip>
      )
    }
    return value ? (
      <Tooltip title={trueLabel}>
        <Lock sx={{ color: 'success.main', fontSize: 18 }} />
      </Tooltip>
    ) : (
      <Tooltip title={falseLabel}>
        <Lock sx={{ color: 'warning.main', fontSize: 18 }} />
      </Tooltip>
    )
  }

  // Render statistic type cell
  const renderStatisticCell = (value: StatisticTypes) => {
    switch (value) {
      case StatisticTypes.Leaderboard:
        return (
          <Tooltip title="Leaderboard">
            <Leaderboard sx={{ color: 'success.main', fontSize: 20 }} />
          </Tooltip>
        )
      case StatisticTypes.CompletionList:
        return (
          <Tooltip title="Completion List">
            <FormatListNumbered sx={{ color: 'info.main', fontSize: 20 }} />
          </Tooltip>
        )
      case StatisticTypes.None:
      default:
        return (
          <Tooltip title="None">
            <Close sx={{ color: 'text.disabled', fontSize: 18 }} />
          </Tooltip>
        )
    }
  }

  const renderCriteriaIcons = (support: { display: boolean; join: boolean; logic: boolean }) => {
    return (
      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
        <Tooltip title="Display Criteria">
          <Box
            sx={{
              width: 24,
              height: 24,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: support.display ? alpha(theme.palette.info.main, 0.15) : 'transparent',
            }}
          >
            <Visibility
              sx={{
                fontSize: 16,
                color: support.display ? 'info.main' : 'text.disabled',
                opacity: support.display ? 1 : 0.3,
              }}
            />
          </Box>
        </Tooltip>
        <Tooltip title="Join Criteria">
          <Box
            sx={{
              width: 24,
              height: 24,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: support.join ? alpha(theme.palette.success.main, 0.15) : 'transparent',
            }}
          >
            <MeetingRoom
              sx={{
                fontSize: 16,
                color: support.join ? 'success.main' : 'text.disabled',
                opacity: support.join ? 1 : 0.3,
              }}
            />
          </Box>
        </Tooltip>
        <Tooltip title="Logic Criteria">
          <Box
            sx={{
              width: 24,
              height: 24,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: support.logic ? alpha(theme.palette.warning.main, 0.15) : 'transparent',
            }}
          >
            <SettingsIcon
              sx={{
                fontSize: 16,
                color: support.logic ? 'warning.main' : 'text.disabled',
                opacity: support.logic ? 1 : 0.3,
              }}
            />
          </Box>
        </Tooltip>
      </Box>
    )
  }

  const stickyColumnWidth = 180
  const categoryColumnWidth = 120

  // Legend components for section headers
  const SettingsLegend = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 1, flexWrap: 'wrap' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Lock sx={{ fontSize: 14, color: 'warning.main' }} />
        <Typography variant="caption" color="text.secondary">Not Allowed</Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Tune sx={{ fontSize: 14, color: 'info.main' }} />
        <Typography variant="caption" color="text.secondary">Configurable</Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Lock sx={{ fontSize: 14, color: 'success.main' }} />
        <Typography variant="caption" color="text.secondary">Forced</Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1, pl: 1, borderLeft: 1, borderColor: 'divider' }}>
        <Leaderboard sx={{ fontSize: 14, color: 'success.main' }} />
        <Typography variant="caption" color="text.secondary">Leaderboard</Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <FormatListNumbered sx={{ fontSize: 14, color: 'info.main' }} />
        <Typography variant="caption" color="text.secondary">Completion List</Typography>
      </Box>
    </Box>
  )

  const CriteriaLegend = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Visibility sx={{ fontSize: 14, color: 'info.main' }} />
        <Typography variant="caption" color="text.secondary">Display</Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <MeetingRoom sx={{ fontSize: 14, color: 'success.main' }} />
        <Typography variant="caption" color="text.secondary">Join</Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <SettingsIcon sx={{ fontSize: 14, color: 'warning.main' }} />
        <Typography variant="caption" color="text.secondary">Logic</Typography>
      </Box>
    </Box>
  )

  const NodesLegend = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Close sx={{ fontSize: 14, color: 'error.main' }} />
        <Typography variant="caption" color="text.secondary">Blocked</Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Check sx={{ fontSize: 14, color: 'success.main' }} />
        <Typography variant="caption" color="text.secondary">Allowed</Typography>
      </Box>
    </Box>
  )

  return (
    <Box
      sx={{
        position: 'relative',
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      {/* Scrollable container */}
      <Box
        sx={{
          overflowX: 'auto',
          overflowY: 'visible',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            minWidth: 'fit-content',
          }}
        >
          {/* Sticky first column */}
          <Box
            sx={{
              position: 'sticky',
              left: 0,
              zIndex: 2,
              bgcolor: 'background.paper',
              borderRight: 1,
              borderColor: 'divider',
              minWidth: stickyColumnWidth,
              width: stickyColumnWidth,
            }}
          >
            {/* Header cell */}
            <Box
              sx={{
                height: 64,
                display: 'flex',
                alignItems: 'center',
                px: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              <Typography variant="subtitle2" fontWeight={600}>
                Feature
              </Typography>
            </Box>

            {/* Settings Section Header */}
            <Box
              sx={{
                height: 36,
                display: 'flex',
                alignItems: 'center',
                px: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.12),
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              <Typography variant="caption" fontWeight={700} color="primary.main" sx={{ textTransform: 'uppercase' }}>
                Settings
              </Typography>
            </Box>

            {/* Settings rows */}
            {settingsFields.map((field) => (
              <Box
                key={field.key}
                sx={{
                  height: 40,
                  display: 'flex',
                  alignItems: 'center',
                  px: 2,
                  borderBottom: 1,
                  borderColor: 'divider',
                }}
              >
                <Typography variant="body2">{field.label}</Typography>
              </Box>
            ))}

            {/* Criteria Section Header */}
            <Box
              sx={{
                height: 36,
                display: 'flex',
                alignItems: 'center',
                px: 2,
                bgcolor: alpha(theme.palette.secondary.main, 0.12),
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              <Typography variant="caption" fontWeight={700} color="secondary.main" sx={{ textTransform: 'uppercase' }}>
                Criteria
              </Typography>
            </Box>

            {/* Criteria rows */}
            {criteriaTypes.map((criteria) => (
              <Tooltip key={criteria.id} title={criteria.description} placement="right">
                <Box
                  sx={{
                    height: 40,
                    display: 'flex',
                    alignItems: 'center',
                    px: 2,
                    borderBottom: 1,
                    borderColor: 'divider',
                    cursor: 'help',
                  }}
                >
                  <Typography variant="body2" noWrap>
                    {criteria.name}
                  </Typography>
                </Box>
              </Tooltip>
            ))}

            {/* Nodes Section Header */}
            <Box
              sx={{
                height: 36,
                display: 'flex',
                alignItems: 'center',
                px: 2,
                bgcolor: alpha(theme.palette.success.main, 0.12),
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              <Typography variant="caption" fontWeight={700} color="success.main" sx={{ textTransform: 'uppercase' }}>
                Nodes
              </Typography>
            </Box>

            {/* Node rows */}
            {filteredNodeTypes.map((node) => (
              <Tooltip key={node.id} title={node.description} placement="right">
                <Box
                  sx={{
                    height: 40,
                    display: 'flex',
                    alignItems: 'center',
                    px: 2,
                    borderBottom: 1,
                    borderColor: 'divider',
                    cursor: 'help',
                  }}
                >
                  <Typography variant="body2" noWrap>
                    {node.displayName}
                  </Typography>
                </Box>
              </Tooltip>
            ))}
          </Box>

          {/* Category columns wrapper */}
          <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
            {/* Header row */}
            <Box sx={{ display: 'flex' }}>
              {categories.map((category) => {
                const isSelected = selectedCategoryId === category.id
                return (
                  <Tooltip key={category.id} title={category.description} placement="top">
                    <Box
                      sx={{
                        minWidth: categoryColumnWidth,
                        width: categoryColumnWidth,
                        height: 64,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        px: 1,
                        bgcolor: isSelected ? alpha(category.color, 0.2) : alpha(theme.palette.primary.main, 0.08),
                        borderBottom: 2,
                        borderRight: 1,
                        borderColor: isSelected ? category.color : 'divider',
                        cursor: 'pointer',
                        '&:hover': {
                          bgcolor: alpha(category.color, 0.12),
                        },
                      }}
                      onClick={() => onCategorySelect(category.id)}
                    >
                      <Typography component="span" sx={{ fontSize: 20, mb: 0.25 }}>
                        {category.iconName}
                      </Typography>
                      <Typography
                        variant="caption"
                        fontWeight={600}
                        noWrap
                        sx={{
                          maxWidth: '100%',
                          textAlign: 'center',
                          color: isSelected ? category.color : 'text.primary',
                        }}
                      >
                        {category.displayName}
                      </Typography>
                    </Box>
                  </Tooltip>
                )
              })}
            </Box>

            {/* Settings Section Header with Legend */}
            <Box
              sx={{
                height: 36,
                display: 'flex',
                alignItems: 'center',
                bgcolor: alpha(theme.palette.primary.main, 0.12),
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              <SettingsLegend />
            </Box>

            {/* Settings rows */}
            {settingsFields.map((field) => (
              <Box key={field.key} sx={{ display: 'flex' }}>
                {categories.map((category) => {
                  const isSelected = selectedCategoryId === category.id
                  return (
                    <Box
                      key={category.id}
                      sx={{
                        minWidth: categoryColumnWidth,
                        width: categoryColumnWidth,
                        height: 40,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderBottom: 1,
                        borderRight: 1,
                        borderColor: 'divider',
                        bgcolor: isSelected ? alpha(category.color, 0.08) : 'transparent',
                        cursor: 'pointer',
                        '&:hover': {
                          bgcolor: alpha(category.color, 0.12),
                        },
                      }}
                      onClick={() => onCategorySelect(category.id)}
                    >
                      {field.isStatistic
                        ? renderStatisticCell(category.statisticTypes)
                        : field.triState
                          ? renderTriStateCell(
                              getSettingValue(category, field.key),
                              field.falseLabel || 'Off',
                              field.nullLabel || 'Creator Choice',
                              field.trueLabel || 'On'
                            )
                          : renderBooleanCell(getSettingValue(category, field.key))}
                    </Box>
                  )
                })}
              </Box>
            ))}

            {/* Criteria Section Header with Legend */}
            <Box
              sx={{
                height: 36,
                display: 'flex',
                alignItems: 'center',
                bgcolor: alpha(theme.palette.secondary.main, 0.12),
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              <CriteriaLegend />
            </Box>

            {/* Criteria rows */}
            {criteriaTypes.map((criteria) => (
              <Box key={criteria.id} sx={{ display: 'flex' }}>
                {categories.map((category) => {
                  const isSelected = selectedCategoryId === category.id
                  const support = getCriteriaSupport(category, criteria.id)
                  return (
                    <Box
                      key={category.id}
                      sx={{
                        minWidth: categoryColumnWidth,
                        width: categoryColumnWidth,
                        height: 40,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderBottom: 1,
                        borderRight: 1,
                        borderColor: 'divider',
                        bgcolor: isSelected ? alpha(category.color, 0.08) : 'transparent',
                        cursor: 'pointer',
                        '&:hover': {
                          bgcolor: alpha(category.color, 0.12),
                        },
                      }}
                      onClick={() => onCategorySelect(category.id)}
                    >
                      {renderCriteriaIcons(support)}
                    </Box>
                  )
                })}
              </Box>
            ))}

            {/* Nodes Section Header with Legend */}
            <Box
              sx={{
                height: 36,
                display: 'flex',
                alignItems: 'center',
                bgcolor: alpha(theme.palette.success.main, 0.12),
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              <NodesLegend />
            </Box>

            {/* Node rows */}
            {filteredNodeTypes.map((node) => (
              <Box key={node.id} sx={{ display: 'flex' }}>
                {categories.map((category) => {
                  const isSelected = selectedCategoryId === category.id
                  return (
                    <Box
                      key={category.id}
                      sx={{
                        minWidth: categoryColumnWidth,
                        width: categoryColumnWidth,
                        height: 40,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderBottom: 1,
                        borderRight: 1,
                        borderColor: 'divider',
                        bgcolor: isSelected ? alpha(category.color, 0.08) : 'transparent',
                        cursor: 'pointer',
                        '&:hover': {
                          bgcolor: alpha(category.color, 0.12),
                        },
                      }}
                      onClick={() => onCategorySelect(category.id)}
                    >
                      {renderBooleanCell(isNodeAllowed(category, node.id))}
                    </Box>
                  )
                })}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
